import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { UIRootState } from '../store/reducers';
import { GameStats } from './GameStats';
import { GameButtons } from './GameButtons.tsx';
import { DesignModal } from './modals/DesignModal/DesignModal.tsx';
import { SettingsModal } from './modals/SettingsModal/SettingsModal.tsx';
import { NewGameModal } from './modals/NewGameModal/NewGameModal.tsx';
import { closeImagePreviewModal, rootElement } from 'ui/index.ts';
import { MissionsModal } from './modals/MissionsModal/MissionsModal.tsx';
import { MissionCompletePopup } from './modals/MissionsModal/components/MissionCompletePopup/index.tsx';
import { StoriesModal } from './modals/StoriesModal/StoriesModal.tsx';
import ImagePreviewModal from './common/ImagePrevideModal/ImagePreviewModal.tsx';
import { MobileBottomTabPanel } from './common/MobileBottomTabPanel/index.tsx';
import { translate } from 'game/translate.ts';
import { Store } from 'game/store/gameStore.ts';

export const GameApp: React.FC = () => {
  const dispatch = useDispatch();

  const { difficulty, moves, time, showDifficulty } = useSelector((state: UIRootState) => state.gameUI.gameStats);
  const settingsOpen = useSelector((state: UIRootState) => state.gameUI.modals.settingsModal.isOpen);
  const designOpen = useSelector((state: UIRootState) => state.gameUI.modals.designModal.isOpen);
  const missionsOpen = useSelector((state: UIRootState) => state.gameUI.modals.missionsModal.isOpen);
  const storiesOpen = useSelector((state: UIRootState) => state.gameUI.modals.storiesModal.isOpen);
  const newGameOpen = useSelector((state: UIRootState) => state.gameUI.modals.newGameModal?.isOpen);
  const tutorialActive = useSelector((state: UIRootState) => state.gameUI.tutorialActive);
  const isInitialized = useSelector((state: UIRootState) => state.gameUI.isInitialized);
  const labels = useSelector((state: UIRootState) => state.gameUI.labels);
  const leftX = useSelector((state: UIRootState) => state.gameUI.gameStats.leftX);
  const buttons = useSelector((state: UIRootState) => state.gameUI.buttons);
  const missionCompletePopupOpened = useSelector((state: UIRootState) => state.gameUI.modals.missionCompletePopup.isOpen);
  const imagePreviewModalOpened = useSelector((state: UIRootState) => state.gameUI.modals.imagePreviewModal.isOpen);
  const imagePreviewModalImages = useSelector((state: UIRootState) => state.gameUI.modals.imagePreviewModal.images);
  const imagePreviewModalInitialSlideIndex = useSelector((state: UIRootState) => state.gameUI.modals.imagePreviewModal.initialSlideIndex);
  const gameUIRef = useRef<HTMLDivElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isLowHeightSecondary, setIsLowHeightSecondary] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const computeIsTablet = () => {
      const isLowHeight = window.innerHeight <= 682;
      const isLandscape = window.matchMedia('(orientation: landscape)').matches;
      return window.matchMedia('(max-width: 1023px)').matches || (isLowHeight && isLandscape);
    };
    const onResize = () => {
      setIsTablet(computeIsTablet());
      setIsLowHeightSecondary(window.matchMedia('(max-height: 719px)').matches);
      setIsMobile(window.matchMedia('(max-width: 758px)').matches);
    };
    setIsTablet(computeIsTablet());
    setIsLowHeightSecondary(window.matchMedia('(max-height: 719px)').matches);
    setIsMobile(window.matchMedia('(max-width: 758px)').matches);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);


  useEffect(() => {
    const html = document.querySelector('html');
      
    if (designOpen || missionsOpen || storiesOpen || newGameOpen || (isMobile && anyPanelOpen)) {
      document.body.style.overflow = 'hidden';
      if (html) {
        html.style.overflow = 'hidden';
      }
    } else {
      document.body.style.overflow = '';
      if (html) {
        html.style.overflow = '';
      }
    }
    
    const canvas = document.querySelector('#solitaire-container canvas');
    
    if (canvas && rootElement) {
      rootElement.style.width = canvas.clientWidth + 'px';
    }
    return () => {
      document.body.style.overflow = '';
      const html = document.querySelector('html');
      if (html) {
        html.style.overflow = '';
      }
    };
  }, [designOpen, missionsOpen, storiesOpen, newGameOpen, isMobile]);

  const handleButtonClick = (buttonKey: string, data?: any) => {
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
    
    document.dispatchEvent(new CustomEvent('ui-button-click', {
      detail: { buttonKey, data }
    }));
  };

  const [statsVisible, setStatsVisible] = useState(true);
  const [statsFading, setStatsFading] = useState(false);
  const anyPanelOpen = useMemo(() => storiesOpen || settingsOpen || designOpen || missionsOpen || newGameOpen, [storiesOpen, settingsOpen, designOpen, missionsOpen, newGameOpen]);
  const blockNewGame = buttons.magicWandDisabled || buttons.hintsDisabled;
  useEffect(() => {
    if (anyPanelOpen) {
      setStatsFading(true);
      const t = setTimeout(() => setStatsVisible(false), 150);
      return () => clearTimeout(t);
    }

    setStatsVisible(true);
    const t = setTimeout(() => setStatsFading(false), 0);
    return () => clearTimeout(t);
  }, [anyPanelOpen]);

  const statsClass = !isTablet && anyPanelOpen ? '.game-ui-stats--panel-expanded' : ''

  const rootClass = [
    'h-100',
    isTablet && anyPanelOpen ? 'tablet-open' : '',
    !isTablet && anyPanelOpen ? 'panel-expanded' : '',
    isMobile && (anyPanelOpen || isTablet) ? 'game-ui--fixed' : ''
  ].filter(Boolean).join(' ');

  const gameButtonsClass = [
    'd-flex',
    'justify-content-center',
    !isTablet && anyPanelOpen && !isLowHeightSecondary ? 'buttons-shrink' : '',
    isTablet && anyPanelOpen ? 'buttons-dim' : ''
  ].filter(Boolean).join(' ');

  const handleCloseAllPanels = useCallback(() => {
    if (settingsOpen) handleButtonClick('close-settings');
    if (designOpen) handleButtonClick('close-design');
    if (missionsOpen) handleButtonClick('close-missions');
    if (storiesOpen) handleButtonClick('close-stories');
    if (mobileMenuOpen) setMobileMenuOpen(false);
    if (newGameOpen) handleButtonClick('close-new-game');
  }, [settingsOpen, designOpen, mobileMenuOpen, missionCompletePopupOpened, missionsOpen, storiesOpen, newGameOpen]);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    const target = e.target as Element | null;
    const insidePanel = target && (target.closest('#game-ui') || target.closest('.body_lock') || target.closest('#whats_new_btn_close') || target.closest('.tablet-buttons') || target.closest('#game-ui-design') || target.id === 'closing-ignore-el' || target.closest('#closing-ignore-el'));
    if ((!insidePanel && !missionCompletePopupOpened) || target?.id === 'force-close-panels') {
      handleCloseAllPanels();
    }
  }, [missionCompletePopupOpened, handleCloseAllPanels]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleCloseAllPanels();
    }
  }, [handleCloseAllPanels]);

  const handleVisibilityChange = useCallback(() => {
    if (document.hidden) {
      handleCloseAllPanels();
    }
  }, [handleCloseAllPanels]);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleCloseAllPanels);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleCloseAllPanels);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  });

  if (!isInitialized || tutorialActive) {
    return null;
  }

  return (
    <>
      <ImagePreviewModal
        isOpen={imagePreviewModalOpened}
        onClose={() => dispatch(closeImagePreviewModal())}
        images={imagePreviewModalImages.map((image, index) => ({
          id: index,
          src: image.src,
          alt: image.alt,
          isUnlocked: image.isUnlocked,
          previewText: image.previewText
        }))}
        initialSlideIndex={imagePreviewModalInitialSlideIndex}
      />
      <div 
        id="game-ui-stats-mobile" 
        className={buttons.magicWandDisabled ? 'magic-wand-active' : ''}
        style={{
          zIndex: 1,
          justifySelf: 'start',
          marginLeft: '5px',
          pointerEvents: 'inherit'
        }}
      >
        {showDifficulty && (
          <h5 id="difficulty-text" style={{
            fontSize: '19px',
            textTransform: 'capitalize',
            fontWeight: 400,
          }} className="text-light mb-0">
            {Store.difficultyBalanced ? translate('balanced') : difficulty}
          </h5>
        )}
        
        <div className="text-center d-flex flex-row" style={{
          top: '5px',
          position: 'absolute',
          left: `${leftX}px`,
          pointerEvents: 'inherit'
        }}>
          <div className="d-flex flex-row" style={{

            gap: '8px',
            alignItems: 'center'
          }}>
            <div id="moves-label" style={{ fontSize: '14px' }} className="text-light opacity-75">
              {labels.movesLabel}
            </div>
            <div className="text-light mb-0" id="moves-count" style={{ fontWeight: 400, fontSize: '18px' }}>
              {moves}
            </div>
          </div>
          
          <div className="d-flex flex-row " style={{

           alignItems: 'center',
            gap: '8px',
            marginLeft: '20px'
          }}>
            <span className="stat-label text-light opacity-75" style={{ fontSize: '14px', alignItems: 'center' }}>{labels.timeLabel}</span>
            <span className="stat-value text-light" style={{ fontWeight: 400, fontSize: '18px' }}>{typeof time === 'number' ? time : time}</span>
          </div>
        </div>
      </div>

      <div 
        id="game-ui" 
        className={rootClass}
        ref={gameUIRef}
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '120px',
          zIndex: 2,
          transition: 'width 0.36s ease-in-out, background 0.36s ease-in-out, border-radius 0.36s ease-in-out'
        }}
      >
        {statsVisible && (
          <div className={`stats-fader${statsFading ? ' fading' : ''}`}>
            <GameStats className={statsClass} />
          </div>
        )}
        <SettingsModal />
        <NewGameModal gameUiRef={gameUIRef} />
        <DesignModal />
        <MissionsModal />
        <StoriesModal />
      </div>

      <MobileBottomTabPanel className={isMobile && anyPanelOpen ? 'mobile-bottom-tab-panel--fixed' : ''} />
      
      <div id="closing-ignore-el" className={`new-game-button ${blockNewGame ? 'new-game-button--disabled' : ''}`} onClick={() => { if (!blockNewGame) handleButtonClick('new-game'); }} style={{ pointerEvents: blockNewGame ? 'none' : 'auto' }}>
        <picture>
          <source srcSet="/assets/menu/new_game.webp" type="image/webp" />
          <img src="/assets/menu/new_game.png" alt="New Game" />
        </picture>
        <span>
          {translate('new_game_button')}
        </span>
      </div>
      <div 
        id="game-buttons" 
        className={gameButtonsClass} 
        style={{
          gap: '25px',
          height: '84px',
          position: 'absolute',
          bottom: 0,
          width: 'calc(100% - 120px)',
          transition: 'width 0.36s ease-in-out'
        }}
      >
        
        <GameButtons />
      </div>
      {buttons.showAutocomplete && (
        <button 
          id="autocompleteButton" 
          onClick={() => handleButtonClick('autocomplete')}
          className="btn d-flex align-items-center justify-content-center text-white"
          style={{
            width: '221px',
            height: '58px',
            transition: 'all 0.2s ease',
            borderRadius: '200px',
            border: '2px solid rgba(255, 255, 255, 0.4)',
            background: 'rgba(0, 0, 0, 0.2)',
            backdropFilter: 'blur(9px)',
            position: 'absolute',
            right: '168px',
            bottom: '28px'
          }}
        >
          {labels.autocompleteText}
        </button>
      )}
      <MissionCompletePopup />
    </>
  );
}; 